import { ProposalsResource } from "@repo/core/services/resources/proposals";
import { ClientResource } from "@repo/core/services/resources/client";
import { apiInstance } from "@repo/core/services/instances/api";
import {
  PROPOSTA_SITUACAO,
  TIPO_PESSOA,
  UpdateProposalInputDTO
} from "@repo/core/services/entities/Proposals";
import type { PreProposalData } from "../store/preProposalStore";
import { cancelPreProposalAlterarSyncDebounce } from "./preProposalAlterarSyncDebounce.service";
import { sanitizePreProposalApiPayload } from "./sanitizePreProposalApiPayload.service";
import { getCleanCPF } from "@repo/core/utils/string.utils";
import { ProposalTypeByCode } from "@repo/core/constants/proposals";
import { canContinueAuthenticatedFlow } from "@repo/core/services/session/sessionExpiration";

const proposalsResource = new ProposalsResource(apiInstance);
const clientResource = new ClientResource(apiInstance);

const pickFirstTrimmedString = (
  ...values: Array<string | undefined | null>
): string => {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) {
      return trimmed;
    }
  }
  return "";
};

const fetchClientNameFromProfileProposals = async (
  strCliente?: string
): Promise<string> => {
  const cleanCPF = getCleanCPF(strCliente || "");

  if (!cleanCPF) {
    return "";
  }

  try {
    const { data } = await clientResource.consultProfileProposals(cleanCPF);
    return pickFirstTrimmedString(
      data?.lstCartoes?.[0]?.strNome,
      (data as { strNome?: string } | undefined)?.strNome
    );
  } catch {
    return "";
  }
};

/**
 * POST /Proposta/Alterar with DATA_CONFERENCE (pendente) for pre-proposal store data.
 * Sends the FULL sanitized snapshot (no diff). Throws on error so callers can
 * decide how to react (e.g. block navigation).
 */
export async function persistPreProposalAlterarDataConferencePendingOrThrow(
  proposal: PreProposalData
): Promise<void> {
  if (!proposal.lngProposta) {
    return;
  }

  cancelPreProposalAlterarSyncDebounce();

  const sanitizedProposal = sanitizePreProposalApiPayload(proposal);
  const fallbackName = sanitizedProposal.strNome?.trim()
    ? ""
    : await fetchClientNameFromProfileProposals(sanitizedProposal.strCliente);
  const strNome = pickFirstTrimmedString(
    sanitizedProposal.strNome,
    fallbackName
  );

  await proposalsResource.editProposal({
    ...(sanitizedProposal as unknown as UpdateProposalInputDTO),
    lngProposta: String(proposal.lngProposta),
    strNome: strNome || undefined,
    strSituacao: PROPOSTA_SITUACAO.DATA_CONFERENCE,
    strTipoPessoa:
      (sanitizedProposal.strTipoPessoa as TIPO_PESSOA | undefined) ||
      TIPO_PESSOA.FISICA,
    strTipoProposta:
      sanitizedProposal.strTipoProposta || ProposalTypeByCode.AMBOS,
    blnManterPendente: true
  });
}

/**
 * POST /Proposta/Alterar with DATA_CONFERENCE (pendente) for pre-proposal store data.
 * Errors are swallowed so background/debounced flows are not blocked.
 * Use {@link persistPreProposalAlterarDataConferencePendingOrThrow} when you need
 * to await the persistence and react to failures (e.g. block screen navigation).
 */
export async function persistPreProposalAlterarDataConferencePending(
  proposal: PreProposalData
): Promise<void> {
  try {
    await persistPreProposalAlterarDataConferencePendingOrThrow(proposal);
  } catch (error) {
    if (!canContinueAuthenticatedFlow()) {
      throw error;
    }

    // Background/debounced policy: never block the flow on persistence errors
  }
}
